import styled, { css, keyframes } from 'styled-components';

import { BrandColors } from 'constants/brand-colors';

const MOBILE = '@media (max-width: 768px)';

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(14, 165, 233, 0);
  }
  50% {
    transform: scale(1.04);
    box-shadow: 0 0.6rem 1.6rem rgba(14, 165, 233, 0.2);
  }
`;

const pulseAnimation = css<{ $enterDelay?: number }>`
  animation: ${pulse} 2.4s ease-in-out infinite;
  animation-delay: ${({ $enterDelay }) => $enterDelay ?? 0}ms;
  transform-origin: center center;
  will-change: transform, box-shadow;
`;

export const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.6rem;
  width: 100%;
  max-width: 50rem;

  ${MOBILE} {
    gap: 1.2rem;
    max-width: 100%;
  }
`;

export const Callout = styled.div`
  text-align: center;
  padding: 0 1rem;
`;

export const CalloutTitle = styled.div`
  font-size: 1.7rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.darkSlate};
  letter-spacing: -0.01rem;
`;

export const CalloutSub = styled.div`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  margin-top: 0.4rem;
  line-height: 1.5;
`;

export const CalloutBad = styled.span`
  color: ${({ theme }) => theme.colors.errorRed};
  font-weight: 700;
`;

export const CalloutGood = styled.span`
  color: ${({ theme }) => theme.colors.accentTextDark};
  font-weight: 700;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.6rem;
  width: 100%;

  ${MOBILE} {
    gap: 1rem;
  }
`;

export const Card = styled.div<{ $isGood?: boolean; $bg?: string }>`
  background-color: ${({ $bg, theme }) => $bg ?? theme.colors.white};
  border: 0.2rem solid
    ${({ $isGood, theme }) => {
      if ($isGood === true) return theme.colors.accent;
      if ($isGood === false) return theme.colors.errorRed;
      return theme.colors.border;
    }};
  border-radius: 1.6rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: ${({ $isGood }) => {
    if ($isGood === true)
      return '0 0.8rem 2.4rem rgba(14, 165, 233, 0.18)';
    if ($isGood === false)
      return '0 0.8rem 2.4rem rgba(239, 68, 68, 0.18)';
    return '0 0.4rem 1.2rem rgba(15, 23, 42, 0.06)';
  }};
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.darkSlate};
`;

export const ChromeBar = styled.div<{ $bg?: string; $color?: string }>`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.8rem;
  font-size: 1.05rem;
  font-weight: 700;
  background-color: ${({ $bg }) => $bg ?? 'transparent'};
  color: ${({ $color, theme }) => $color ?? theme.colors.darkSlate};
`;

export const ChromeSpacer = styled.div`
  flex: 1;
`;

export const ChromeIconCluster = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
`;

export const Avatar = styled.div<{
  $size?: number;
  $accent?: string;
  $border?: string;
  $photoUrl?: string;
}>`
  position: relative;
  width: ${({ $size }) => $size ?? 4.4}rem;
  height: ${({ $size }) => $size ?? 4.4}rem;
  border-radius: 50%;
  background-color: ${({ $accent, theme }) => $accent ?? theme.colors.accent};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: 700;
  flex-shrink: 0;
  ${({ $border }) => ($border ? `border: 0.3rem solid ${$border};` : '')}
  ${({ $photoUrl }) =>
    $photoUrl
      ? `
        background-image: url(${$photoUrl});
        background-size: cover;
        background-position: center;
        color: transparent;
      `
      : ''}
`;

export const FollowBadge = styled.div`
  position: absolute;
  bottom: -0.4rem;
  left: 50%;
  transform: translateX(-50%);
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 50%;
  background-color: ${BrandColors.tiktokMagenta};
  color: #ffffff;
  font-size: 1.2rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0.2rem solid #ffffff;
`;

export const BioLineBad = styled.div`
  font-size: 1.05rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.errorRed};
  display: flex;
  align-items: center;
  gap: 0.3rem;
  min-width: 0;

  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    text-decoration: line-through;
    text-decoration-color: ${({ theme }) => theme.colors.errorRed};
    text-decoration-thickness: 0.2rem;
  }
`;

export const BioLineGood = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.accent};
  display: flex;
  align-items: center;
  gap: 0.3rem;
  min-width: 0;

  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
`;

export const LinkBoxGood = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.3rem;
  margin-top: 0.4rem;
  padding: 0.5rem 0.7rem;
  background-color: ${({ theme }) => theme.colors.accentLight};
  border: 0.15rem solid ${({ theme }) => theme.colors.accentBorder};
  border-radius: 0.6rem;
  font-size: 1.05rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.accentTextDark};
  width: 100%;
  min-width: 0;
`;

export const LinkBoxGoodPill = styled.span`
  flex-shrink: 0;
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: 0.06rem;
  text-transform: uppercase;
  padding: 0.15rem 0.5rem;
  border-radius: 999rem;
  background-color: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.white};
`;

export const LinkBoxGoodText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  min-width: 0;
`;

const ALERT_TRANSITION = '0.45s ease';

export const AnimatedAlertBox = styled.div<{
  $phase: 'bad' | 'good';
  $enterDelay?: number;
}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.3rem;
  margin-top: 0.4rem;
  padding: 0.5rem 0.7rem;
  border-radius: 0.6rem;
  font-size: 1.05rem;
  font-weight: 800;
  width: 100%;
  min-width: 0;

  background-color: ${({ $phase, theme }) =>
    $phase === 'good' ? theme.colors.accentLight : theme.colors.errorBg};
  border: 0.15rem solid
    ${({ $phase, theme }) =>
      $phase === 'good' ? theme.colors.accentBorder : theme.colors.errorBorder};
  color: ${({ $phase, theme }) =>
    $phase === 'good'
      ? theme.colors.accentTextDark
      : theme.colors.errorRed};

  transition:
    background-color ${ALERT_TRANSITION},
    border-color ${ALERT_TRANSITION},
    color ${ALERT_TRANSITION};

  ${({ $phase }) => $phase === 'good' && pulseAnimation}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const AnimatedAlertPill = styled.span<{ $phase: 'bad' | 'good' }>`
  flex-shrink: 0;
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: 0.06rem;
  text-transform: uppercase;
  padding: 0.15rem 0.5rem;
  border-radius: 999rem;
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ $phase, theme }) =>
    $phase === 'good' ? theme.colors.accent : theme.colors.errorRed};
  transition: background-color ${ALERT_TRANSITION};
`;

export const AnimatedAlertText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  min-width: 0;
`;

export const VerdictBadge = styled.span<{ $isGood?: boolean }>`
  align-self: flex-start;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.05rem;
  text-transform: uppercase;
  padding: 0.2rem 0.6rem;
  border-radius: 999rem;
  ${({ $isGood, theme }) =>
    $isGood
      ? `background-color: ${theme.colors.successBgSoft}; color: ${theme.colors.successText};`
      : `background-color: ${theme.colors.errorBg}; color: ${theme.colors.errorRed};`}
`;

export const IgTopBar = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0.6rem 1rem;
  color: ${({ theme }) => theme.colors.darkSlate};
`;

export const IgTopHandle = styled.span`
  font-size: 1.2rem;
  font-weight: 700;
  text-align: center;
`;

export const IgTopRight = styled.div`
  justify-self: end;
`;

export const IgStoryRing = styled.div`
  background: conic-gradient(
    from 0deg,
    #feda75,
    #fa7e1e,
    #d62976,
    #962fbf,
    #4f5bd5,
    #feda75
  );
  padding: 0.2rem;
  border-radius: 50%;
  display: inline-block;
`;

export const IgStoryRingInner = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  padding: 0.2rem;
  border-radius: 50%;
  display: inline-block;
`;

export const IgIdentityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 0.4rem 1rem 0.4rem;
`;

export const IgStatRow = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-around;
`;

export const IgStatCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const IgStatNumber = styled.span`
  font-size: 1.3rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.darkSlate};
`;

export const IgStatLabel = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.darkSlate};

  ${MOBILE} {
    display: none;
  }
`;

export const IgNameBlock = styled.div`
  padding: 0 1rem 0.4rem;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`;

export const IgDisplayName = styled.div`
  font-size: 1.15rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.darkSlate};
`;

export const IgCategory = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.slateGrey};
`;

export const IgBioBullet = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.darkSlate};
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const IgLink = styled.a`
  font-size: 1rem;
  font-weight: 600;
  color: #00376b;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
`;

export const IgLinkBad = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.3rem;
  margin-top: 0.4rem;
  padding: 0.5rem 0.7rem;
  background-color: ${({ theme }) => theme.colors.errorBg};
  border: 0.15rem solid ${({ theme }) => theme.colors.errorBorder};
  border-radius: 0.6rem;
  font-size: 1.05rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.errorRed};
  width: 100%;
  min-width: 0;
`;

export const IgLinkBadIcon = styled.span`
  font-size: 1.2rem;
  line-height: 1;
  flex-shrink: 0;
`;

export const IgLinkBadText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  min-width: 0;
`;

export const IgLinkBadPill = styled.span`
  flex-shrink: 0;
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: 0.06rem;
  text-transform: uppercase;
  padding: 0.15rem 0.5rem;
  border-radius: 999rem;
  background-color: ${({ theme }) => theme.colors.errorRed};
  color: ${({ theme }) => theme.colors.white};
`;

export const IgFollowedBy = styled.div`
  padding: 0.3rem 1rem 0;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  & strong {
    color: ${({ theme }) => theme.colors.darkSlate};
    font-weight: 600;
  }
`;

export const IgActionRow = styled.div`
  display: flex;
  gap: 0.4rem;
  padding: 0.6rem 1rem 1rem;
`;

export const IgButton = styled.div<{ $primary?: boolean }>`
  flex: 1;
  font-size: 0.95rem;
  font-weight: 700;
  text-align: center;
  padding: 0.45rem;
  border-radius: 0.6rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  ${({ $primary, theme }) =>
    $primary
      ? `background-color: ${theme.colors.surfaceSoft}; color: ${theme.colors.darkSlate}; border: 0.1rem solid ${theme.colors.border};`
      : `background-color: ${theme.colors.surfaceSoft}; color: ${theme.colors.darkSlate}; border: 0.1rem solid ${theme.colors.border};`}
`;

export const IgIconButton = styled.div`
  width: 3rem;
  font-size: 0.95rem;
  font-weight: 700;
  text-align: center;
  padding: 0.45rem;
  border-radius: 0.6rem;
  background-color: ${({ theme }) => theme.colors.surfaceSoft};
  color: ${({ theme }) => theme.colors.darkSlate};
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const TtScreen = styled.div`
  background-color: #ffffff;
  color: ${({ theme }) => theme.colors.darkSlate};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.4rem 1rem 1rem;
  gap: 0.5rem;
`;

export const TtTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-self: stretch;
  align-items: center;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.darkSlate};
`;

export const TtCenteredHandle = styled.div`
  font-size: 1.2rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.darkSlate};
  margin-top: 0.6rem;
`;

export const TtStatRow = styled.div`
  display: flex;
  gap: 1.4rem;
  margin: 0.4rem 0;
`;

export const TtStatCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const TtStatNumber = styled.span`
  font-size: 1.2rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.darkSlate};
`;

export const TtStatLabel = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.slateGrey};

  ${MOBILE} {
    display: none;
  }
`;

export const TtActionRow = styled.div`
  display: flex;
  gap: 0.4rem;
  align-self: stretch;
  margin-top: 0.2rem;
`;

export const TtFollowButton = styled.div`
  flex: 1;
  background-color: ${BrandColors.tiktokMagenta};
  color: #ffffff;
  font-size: 1.05rem;
  font-weight: 700;
  text-align: center;
  padding: 0.55rem;
  border-radius: 0.5rem;
`;

export const TtMessageButton = styled.div`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.surfaceSoft};
  color: ${({ theme }) => theme.colors.darkSlate};
  font-size: 1.05rem;
  font-weight: 700;
  text-align: center;
  padding: 0.55rem;
  border-radius: 0.5rem;
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
`;

export const TtBioBlock = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  align-items: center;
  text-align: center;
  margin-top: 0.4rem;
`;

export const TtBioText = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.darkSlate};
`;

export const FbCover = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  height: 5.2rem;
`;

export const FbCoverTile = styled.div<{ $gradient: string }>`
  background: ${({ $gradient }) => $gradient};
`;

export const FbAvatarOverlap = styled.div`
  display: flex;
  padding: 0 1rem;
  margin-top: -3.2rem;
`;

export const FbAvatarRing = styled.div`
  padding: 0.3rem;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.white};
`;

export const FbNameBlock = styled.div`
  padding: 0.6rem 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

export const FbName = styled.div`
  font-size: 1.4rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.darkSlate};
`;

export const FbStats = styled.div`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  font-weight: 600;
`;

export const FbBio = styled.div`
  padding: 0.6rem 1rem 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.darkSlate};
`;

export const FbCreatorRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.darkSlate};
`;

export const FbActionRow = styled.div`
  display: flex;
  gap: 0.4rem;
  padding: 0.4rem 1rem 0.4rem;
`;

export const FbPrimary = styled.div`
  flex: 1;
  background-color: ${BrandColors.facebook};
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
  text-align: center;
  padding: 0.55rem;
  border-radius: 0.6rem;
`;

export const FbSecondary = styled.div`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.surfaceSoft};
  color: ${({ theme }) => theme.colors.darkSlate};
  font-size: 1rem;
  font-weight: 700;
  text-align: center;
  padding: 0.55rem;
  border-radius: 0.6rem;
`;

export const FbTabRow = styled.div`
  display: flex;
  gap: 1.2rem;
  padding: 0.6rem 1rem 0.8rem;
  border-top: 0.1rem solid ${({ theme }) => theme.colors.border};
  margin-top: 0.2rem;

  ${MOBILE} {
    gap: 0.4rem;
    padding: 0.5rem 0.6rem 0.6rem;
    justify-content: space-between;
  }
`;

export const FbTab = styled.span<{ $active?: boolean }>`
  font-size: 1.05rem;
  font-weight: 700;
  padding: 0.3rem 0.8rem;
  border-radius: 999rem;
  white-space: nowrap;
  ${({ $active, theme }) =>
    $active
      ? `background-color: ${theme.colors.accentLight}; color: ${theme.colors.accentTextDark};`
      : `color: ${theme.colors.darkSlate};`}

  ${MOBILE} {
    font-size: 0.9rem;
    padding: 0.25rem 0.5rem;
  }
`;

export const FbVerdict = styled.div`
  padding: 0 1rem 1rem;
  display: flex;
`;

export const WaHeader = styled.div`
  background-color: #075e54;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.8rem;
  font-size: 1.05rem;
`;

export const WaIdentity = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.2rem 1rem 0.8rem;
  gap: 0.4rem;
`;

export const WaName = styled.div`
  font-size: 1.4rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.darkSlate};
`;

export const WaPhone = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.slateGrey};
`;

export const WaActionRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  padding: 0 1rem 1rem;
`;

export const WaActionTile = styled.div`
  background-color: ${({ theme }) => theme.colors.surfaceSoft};
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  border-radius: 0.8rem;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  color: #075e54;
  font-size: 0.85rem;
  font-weight: 600;
`;

export const WaSectionLabel = styled.div`
  padding: 0.6rem 1rem 0.4rem;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.05rem;
`;

export const WaAboutBlock = styled.div`
  padding: 0 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

export const WaAboutText = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.darkSlate};
`;

export const WaDivider = styled.div`
  height: 0.1rem;
  background-color: ${({ theme }) => theme.colors.border};
`;
